﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// The form component allows us to wrap a set of input fields and capture any button or form submit
// (through i.e. <enter> in input field) event using a singel onSubmit action on the form.

import * as React from "react";

// ----------------------------------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------------------------------

interface FormProps {
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    isValid?: boolean;
    className?: string;
    children?: React.ReactNode;
}

// ----------------------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------------------

const Form: React.FunctionComponent<FormProps> = ({
    onSubmit,
    children,
    className,
    isValid = true,
}) => {
    const formRef = React.useRef<HTMLFormElement>(undefined);

    const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isValid) {
            const formElem = e.target as HTMLFormElement;
            if (formElem.contains(document.activeElement)) {
                const submitActive =
                    (document.activeElement as HTMLInputElement)?.type ==
                    "submit";
                if (submitActive) {
                    onSubmit(e);
                } else {
                    (document.activeElement as HTMLElement)?.blur();
                    const submitButton: HTMLButtonElement =
                        formRef.current.querySelector('[type="submit"]');
                    setTimeout(() => submitButton.click(), 0);
                }
            } else {
                onSubmit(e);
            }
        }
    };

    return (
        <form
            className={className}
            ref={formRef}
            action="#"
            onSubmit={(x) => onFormSubmit(x)}
        >
            {children}
        </form>
    );
};

export default Form;
export { Form };
